import React, { useEffect, useState } from 'react'
import { IMigrationInfo } from 'shared/types/masters'
import { IUpdateInfo, migrateVersions } from 'shared/api/mutations'
import { Box, Button } from '@mui/material'
import { ConfirmCommitButton } from 'shared/ui'
import { MigrationStatusInfo } from './MigrationStatusInfo'
import { openTab } from '../../../Helpers'

function goToMapping({ fromVersion, toVersion }: IMigrationInfo) {
  if (!fromVersion || !toVersion) return
  openTab(
    `/version_mapping/from/${fromVersion.treeId}:${fromVersion.version}/to/${toVersion.treeId}:${toVersion.version}`,
  )
}

export function NeedsMigration({
  needsMigrate,
}: {
  needsMigrate: IMigrationInfo[]
}) {
  const [migrated, setMigrated] = useState(false)
  const [migrating, setMigrating] = useState<IMigrationInfo[]>([])
  const [skipping, setSkipping] = useState<IMigrationInfo[]>([])
  const [blockMigrate, setBlockMigrate] = useState(false)

  useEffect(() => {
    setMigrating(needsMigrate)
  }, [needsMigrate])

  useEffect(() => {
    const block = !!migrating.find((info) => info.missingMapping)
    setBlockMigrate(block)
  }, [migrating])

  if (!needsMigrate.length) return null

  const skipMigrate = (info: IMigrationInfo) => {
    setSkipping((prev) => [...prev, info])
    setMigrating((prev) =>
      prev.filter((p) => p.master.masterId !== info.master.masterId),
    )
  }

  const unSkipMigrate = (info: IMigrationInfo) => {
    setMigrating((prev) => [...prev, info])
    setSkipping((prev) =>
      prev.filter((p) => p.master.masterId !== info.master.masterId),
    )
  }

  const migrateAll = () => {
    setMigrated(true)
    const updateInfo: IUpdateInfo[] = migrating.map((info) => {
      return {
        destinationVersion: {
          treeId: info.toVersion!.treeId,
          version: info.toVersion!.version,
          name: info.toVersion!.name,
          title: info.toVersion!.title,
        },
        versionToUpdate: {
          version: info.fromVersion!.version,
          name: info.fromVersion!.name,
        },
      }
    })
    return migrateVersions(updateInfo)
  }

  return (
    <Box padding="5px" border={'solid 1px'}>
      <Box>
        <strong>Needs Migration</strong>
        {migrated && (
          <Box>
            You have initiated migrations. Please wait until these migrations
            are complete before starting more. You need to refresh the page to
            check if any are still in process.
          </Box>
        )}
      </Box>
      <Box>
        <ConfirmCommitButton
          onClick={migrateAll}
          disabled={migrated || blockMigrate}
        >
          Migrate All
        </ConfirmCommitButton>
      </Box>
      <InfoDisplay
        infos={migrating}
        title={'Migrating These'}
        action={{ cta: 'Skip', func: skipMigrate }}
      />
      <InfoDisplay
        infos={skipping}
        title={'Skipping These'}
        action={{ cta: 'UnSkip', func: unSkipMigrate }}
      />
    </Box>
  )
}

function InfoDisplay({
  infos,
  title,
  action: { cta, func },
}: {
  infos: IMigrationInfo[]
  title: string
  action: {
    cta: string
    func: (i: IMigrationInfo) => void
  }
}) {
  if (!infos.length) return null
  return (
    <Box padding="10px">
      <Box>
        <strong>{title}</strong>
      </Box>
      {infos.map((mInfo) => {
        const { master, fromVersion, toVersion, schoolCounts } = mInfo
        return (
          <Box key={master.masterId} padding={'5px'}>
            <Box>{master.title}</Box>
            <Box fontSize={12} color={'grey'}>
              {`${schoolCounts?.fromVersion} schools from: ${fromVersion?.name} to: ${toVersion?.name}`}
              <MigrationStatusInfo migrationInfo={mInfo} />
            </Box>
            <Box>
              <Button onClick={() => goToMapping(mInfo)}>Go To Mapping</Button>
              <Button onClick={() => func(mInfo)}>{cta}</Button>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}
